import { useCallback, useMemo, useRef, useState } from 'react';
import { useCrudConfig } from './provider';
import { applyCrudUrlState, CrudSort, CrudUrlState, decodeCrudUrlState, FilterState } from './urlState';
import { useDebouncedValue } from './useDebouncedValue';
import { warnInDev } from './warnInDev';

/** One debounce value for every CRUD list in the product. */
export const DEFAULT_DEBOUNCE_MS = 700;

/** Default page size for every CRUD list in the product. */
export const DEFAULT_PAGE_SIZE = 20;

export interface CrudFiltersState {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  search: string;
  setSearch: (value: string) => void;
  /** What the query should use. Lags `search` by debounceMs. */
  debouncedSearch: string;
  sort: CrudSort[];
  setSort: (next: CrudSort[]) => void;
  /** The active chips, straight from the URL — one entry per field (spec §7.3). */
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  /**
   * The chip set the REQUEST carries, lagging `filters` by debounceMs. Editing a chip commits
   * on every keystroke, so without this the list was refetched once per character typed.
   */
  debouncedFilters: FilterState;
  /**
   * Writes several parts of the URL state in exactly ONE write, unlike chaining setters —
   * which is what makes it safe to call from a single event handler. Does exactly what it
   * is given: unlike setSearch/setSort it does not implicitly reset the page; pass
   * `page: 1` in the same partial when the change should go back to the first page.
   */
  patch: (partial: Partial<CrudUrlState>) => void;
  /** Resets search, sort and filters. Deliberately not column preferences. */
  clear: () => void;
}

export function useCrudFilters(
  options: { debounceMs?: number; pageSize?: number; urlKeyPrefix?: string } = {}
): CrudFiltersState {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const urlKeyPrefix = options.urlKeyPrefix;
  const { useUrlState } = useCrudConfig();
  // Injected by CrudConfigProvider and stable for the provider's lifetime; the rule cannot
  // see that. See apps/portal/src/crud/CrudProviders.tsx.
  // eslint-disable-next-line react/hooks
  const [params, writeParams] = useUrlState();

  // pageSize is a per-user preference, not part of a shared view, so it stays local — see
  // the spec's §8.1 example URL, which carries page, s and sort and nothing else. The
  // filters, by contrast, ARE part of the shared view and live in the URL (spec §8.1):
  // a filtered list must be shareable and survive a reload.
  const [pageSize, setPageSize] = useState(options.pageSize ?? DEFAULT_PAGE_SIZE);

  const urlState = useMemo(() => decodeCrudUrlState(params, urlKeyPrefix), [params, urlKeyPrefix]);

  // useDebouncedValue seeds its state with the value it is first given, so a link arriving
  // with ?s=chick filters on the first render instead of showing the unfiltered list for
  // 700ms and then blinking.
  const debouncedSearch = useDebouncedValue(urlState.search, debounceMs);

  // Chip values are typed a character at a time — a text chip commits on every keystroke — so
  // the set that reaches the fetch layer waits out the same window the search term does. The
  // URL and the chip bar keep the immediate state; only the request lags.
  const debouncedFilters = useDebouncedValue(urlState.filters, debounceMs);

  // One URL write per tick, enforced below: the binding's setter takes a concrete
  // URLSearchParams rather than a functional update (react-router-dom@6.0.0 cannot be relied
  // on for the functional form), so two writes that both read this same `params` leave the
  // second silently clobbering the first. The flag resets at the top of every render — a
  // write that a render has separated read fresh params and is safe — so a warning here
  // means two writes landed between two renders, which is exactly the stale-params case.
  const wroteSinceRenderRef = useRef(false);
  // react/refs is off for this one line on purpose. Writing a ref during render is normally
  // unsafe because a discarded concurrent render tears it — but this ref feeds nothing except
  // the dev-only warning below, never render output, so the worst a torn write costs is one
  // spurious or missed warning. Any concurrent-safe alternative (state, an effect) would
  // either loop or reset too late to detect two writes between two renders.
  // eslint-disable-next-line react/refs
  wroteSinceRenderRef.current = false;

  const write = useCallback(
    (partial: Partial<CrudUrlState>) => {
      if (wroteSinceRenderRef.current) {
        warnInDev(
          'the URL was written twice in one tick; the second write read the same stale params and ' +
            'silently clobbered the first. Make all URL changes in one write — useCrudFilters().patch() ' +
            'changes several parts at once.'
        );
      }
      wroteSinceRenderRef.current = true;
      writeParams(applyCrudUrlState(params, { ...decodeCrudUrlState(params, urlKeyPrefix), ...partial }, urlKeyPrefix));
    },
    [params, writeParams, urlKeyPrefix]
  );

  const setPage = useCallback((page: number) => write({ page }), [write]);

  // Paginate to page 3, then search or refilter: without resetting the page here, the
  // request goes out with page=3 against a result set that may not have a page 3, returns
  // nothing, and the pagination bar vanishes with no way back.
  const setSearch = useCallback((value: string) => write({ search: value, page: 1 }), [write]);
  const setSort = useCallback((next: CrudSort[]) => write({ sort: next, page: 1 }), [write]);

  // One write: the chip state and the page reset travel together, exactly like setSearch.
  const setFilters = useCallback((next: FilterState) => write({ filters: next, page: 1 }), [write]);

  const clear = useCallback(() => write({ page: 1, search: '', sort: [], filters: {} }), [write]);

  return {
    page: urlState.page,
    setPage,
    pageSize,
    setPageSize,
    search: urlState.search,
    setSearch,
    debouncedSearch,
    sort: urlState.sort,
    setSort,
    filters: urlState.filters,
    setFilters,
    debouncedFilters,
    patch: write,
    clear,
  };
}
